import { useCallback, useEffect, useRef, useState } from "react";
import { DND_TYPES } from "@constants";

// 드래그 호버 시 접힌 폴더를 자동으로 펼치는 지연시간 (ms)
const DRAG_HOVER_EXPAND_DELAY = 300;

// children 배열에서 특정 폴더의 바로 앞 형제 ID (없으면 null = 맨 앞)
const previousSiblingOf = (siblings, folderId) => {
    const index = siblings.indexOf(folderId);
    return index > 0 ? siblings[index - 1] : null;
};

/**
 * 드롭 좌표(대상 폴더·position)를 폴더 이동 실행에 필요한 값들로 해석하는 순수 함수.
 * 원위치 캡처를 포함하므로 낙관 반영(updateMovedFolder의 in-place 뮤테이션) 전에 호출해야 한다.
 *
 * @param {Object} folderMap folderTreeView.folderList.data
 * @param {string} draggedFolderId 드래그된 폴더 ID
 * @param {Object} targetFolder 드롭된 행의 폴더
 * @param {"before"|"after"|"into"|"intoFirst"} position 드롭 존 판정 결과
 * @returns {Object|null} 무효 드롭(폴더 없음)·동일 위치 no-op이면 null, 아니면
 *   { draggedFolder, toFolder, originalPosition, isReorder, isDeferredReveal, moveParams }
 */
const resolveFolderDropPlan = (folderMap, draggedFolderId, targetFolder, position) => {
    const draggedFolder = folderMap[draggedFolderId];
    if (!draggedFolder) return null;

    // 드롭 위치 → 이동 후 새 부모(toFolderId) + 그 안에서의 순서(previousId — undefined: 맨 뒤 / null: 맨 앞 / ID: 그 뒤)
    // toFolder 확정 전이므로 맵 접근은 옵셔널 체이닝 유지 (부모 소실 시 아래 가드에서 폐기)
    let toFolderId;
    let previousId;
    switch (position) {
        case "before": {
            toFolderId = targetFolder.parent;
            // 같은 부모 내 재정렬 시 자기 자신이 이전 형제로 잡히면 제자리 이동이 되므로 제외 후 계산
            const siblings = (folderMap[toFolderId]?.children ?? []).filter((id) => id !== draggedFolderId);
            previousId = previousSiblingOf(siblings, targetFolder.id);
            break;
        }
        case "after":
            toFolderId = targetFolder.parent;
            previousId = targetFolder.id;
            break;
        case "intoFirst":
            toFolderId = targetFolder.id;
            previousId = null;
            break;
        case "into":
        default:
            toFolderId = targetFolder.id;
            break;
    }

    const toFolder = folderMap[toFolderId];
    if (!toFolder) return null;

    // 실패 시 원복에 필요한 원위치 스냅샷 — 낙관 반영이 folderMap을 in-place 뮤테이션하므로 원시값으로 동결 캡처
    const originalPosition = {
        parentId: draggedFolder.parent,
        grandParentId: folderMap[draggedFolder.parent]?.parent,
        previousId: previousSiblingOf(folderMap[draggedFolder.parent]?.children ?? [], draggedFolderId),
    };

    // 동일 위치 드롭 no-op
    if (position === "into") {
        if (originalPosition.parentId === toFolder.id) return null;
    } else if (originalPosition.parentId === toFolder.id && originalPosition.previousId === previousId) {
        return null;
    }

    // folders:move API 본문
    const moveParams = {
        from: { id: draggedFolderId, parent: { id: originalPosition.parentId } },
        to: { id: toFolder.id },
    };
    if (toFolder.parent) moveParams.to.parent = { id: toFolder.parent }; // 루트(드라이브) 대상은 parent 생략
    if (position !== "into") moveParams.position = { previousId }; // into는 position 생략 → 서버 기본 배치(맨 뒤)

    return {
        draggedFolder,
        toFolder,
        originalPosition,
        isReorder: originalPosition.parentId === toFolder.id,
        // 접힌(미로드·재접힘) 또는 하위 조회 중인 대상은 낙관 삽입 시 스켈레톤과 겹치거나 stale 응답에 덮여 사라질 수 있어
        // 지연 표시 대상 (into 외 position은 대상이 보이는 상태에서만 발생)
        isDeferredReveal: position === "into" && (toFolder.isFold || toFolder.pending),
        moveParams,
    };
};

/**
 * 폴더 트리 행의 드래그앤드롭 일체를 담당하는 훅.
 * - 폴더 드래그(순서 변경·하위 이동)와 목록 아이템(에셋/컬렉션) 드롭을 dataTransfer 타입으로 라우팅
 * - into 하이라이트·드롭 위치 인디케이터 상태, 접힌 폴더 hover-expand 타이머 관리
 *
 * @returns {{ dragProps: object, isIntoDropTarget: boolean, dropPosition: string|null, isDragSource: boolean }}
 *          dragProps는 행 루트에 스프레드할 draggable/onDrag* 핸들러 묶음,
 *          isDragSource는 이 행이 현재 잡혀있는(드래그 중인) 폴더인지 여부
 */
const useTreeItemDnd = ({
    folder,
    folderMap,
    foldDisabled,
    canEdit,
    selectedFolderId,
    onDropTreeItem,
    onDropFolderItem,
    onToggleFolderFold,
    draggingFolderIdRef,
    dragPreviewRef,
    movePendingFolderIds,
    activeDropRowRef,
}) => {
    // "before" | "after" | "into" | "intoFirst" | null — 이 행에 표시할 드롭 피드백 일체 (아이템 드래그는 into 고정)
    const [dropPosition, setDropPosition] = useState(null);
    // 이 행이 드래그 소스(잡은 폴더)인지 — 공유 ref(draggingFolderIdRef)는 리렌더를 유발하지 않아 스타일링에 쓸 수 없고,
    // dragstart/dragend가 모두 소스 행에서 발생하므로 소스 행 훅 인스턴스의 로컬 state가 리렌더 범위상 최소
    const [isDragSource, setIsDragSource] = useState(false);
    const dragHoverExpandTimerRef = useRef(null);

    const isIntoDropTarget = dropPosition === "into";
    const isMovePending = !!movePendingFolderIds?.includes(folder?.id);
    const isItemDroppable = !!onDropTreeItem && !!folder?.id && canEdit;
    // 이동 요청이 떠 있는 폴더는 위치가 확정되지 않아 드롭 대상·드래그 소스 모두에서 제외
    const isFolderDroppable = !!onDropFolderItem && !!folder?.id && canEdit && !isMovePending;
    const isFolderDraggable = isFolderDroppable && folder?.depth > 0;

    // 포지티브 라우팅 — 폴더/목록 아이템 마커가 없거나(OS 파일 등) 해당 드롭이 비활성인 드래그는 null
    const getDroppableDragKind = useCallback(
        ({ dataTransfer: { types } }) => {
            if (types.includes(DND_TYPES.FOLDER)) return isFolderDroppable ? "folder" : null;
            if (types.includes(DND_TYPES.LIST_ITEM)) return isItemDroppable ? "item" : null;
            return null;
        },
        [isFolderDroppable, isItemDroppable],
    );

    const isInvalidFolderDropTarget = useCallback(() => {
        const draggedId = draggingFolderIdRef?.current;
        if (!draggedId || draggedId === folder?.id) return true;
        // 자기 자손으로의 드롭 금지 — parent 체인 역추적
        let current = folderMap?.[folder?.id];
        while (current?.parent) {
            if (current.parent === draggedId) return true;
            current = folderMap[current.parent];
        }
        return false;
    }, [draggingFolderIdRef, folder?.id, folderMap]);

    const handleDragStart = useCallback(
        (e) => {
            e.stopPropagation();
            // setData는 types 마커 등록용 — ID 참조는 ref로 통일 (dragover에서는 getData가 protected mode라 불가)
            e.dataTransfer.setData(DND_TYPES.FOLDER, folder.id);
            e.dataTransfer.effectAllowed = "move";
            const previewEl = dragPreviewRef?.current;
            if (previewEl) {
                // 폴더명은 DOM에 직접 주입 — state 리렌더는 dragstart의 스냅샷 캡처에 못 맞춤
                const labelEl = previewEl.querySelector(".folder-drag-preview-label");
                if (labelEl) labelEl.textContent = folder.name;
                e.dataTransfer.setDragImage(previewEl, 0, 0);
            }
            if (draggingFolderIdRef) draggingFolderIdRef.current = folder.id;
            setIsDragSource(true);
        },
        [folder?.id, folder?.name, draggingFolderIdRef, dragPreviewRef],
    );

    const cancelDragHoverExpand = useCallback(() => {
        if (dragHoverExpandTimerRef.current) {
            clearTimeout(dragHoverExpandTimerRef.current);
            dragHoverExpandTimerRef.current = null;
        }
    }, []);

    // 드롭 피드백(하이라이트·인디케이터·hover-expand 예약) 표시만 해제 — 소유권은 건드리지 않으므로
    // 트리 루트(드래그 종료 감지)와 다음 소유 행이 이 행을 끌 때 쓴다
    const clearDropFeedback = useCallback(() => {
        setDropPosition(null);
        cancelDragHoverExpand();
    }, [cancelDragHoverExpand]);

    // 이 행이 스스로 해제 — 등록해둔 소유권까지 반납한다 (이탈·무효 대상·드롭·드래그 종료 공통)
    const resetDropFeedback = useCallback(() => {
        if (activeDropRowRef?.current?.folderId === folder?.id) activeDropRowRef.current = null;
        clearDropFeedback();
    }, [activeDropRowRef, folder?.id, clearDropFeedback]);

    // 강조 소유권을 이 행으로 가져온다 — 이전 소유 행을 반드시 끄므로 트리 전체에서 강조 행은 항상 최대 1개.
    // 트리 단위 state로 올리지 않는 이유는 dragover마다 트리 전체가 리렌더되기 때문 — 이 방식은 이전·현재 두 행만 리렌더된다
    const claimDropFeedback = useCallback(
        (position) => {
            const activeRow = activeDropRowRef?.current;
            if (activeRow && activeRow.folderId !== folder?.id) activeRow.clear();
            if (activeDropRowRef) activeDropRowRef.current = { folderId: folder?.id, clear: clearDropFeedback };
            setDropPosition(position);
        },
        [activeDropRowRef, folder?.id, clearDropFeedback],
    );

    const handleDragEnd = useCallback(() => {
        if (draggingFolderIdRef) draggingFolderIdRef.current = null;
        setIsDragSource(false);
        resetDropFeedback();
    }, [draggingFolderIdRef, resetDropFeedback]);

    const scheduleDragHoverExpand = useCallback(() => {
        if (foldDisabled) return;
        if (!folder?.isFold) return;
        // 비어있다고 확인된 폴더는 건너뜀. 아직 fetch 안 한 폴더는 펼침 시 API 호출됨.
        if (folder?.isEmpty === true) return;
        if (dragHoverExpandTimerRef.current) return;
        dragHoverExpandTimerRef.current = setTimeout(() => {
            dragHoverExpandTimerRef.current = null;
            onToggleFolderFold(folder);
        }, DRAG_HOVER_EXPAND_DELAY);
    }, [foldDisabled, folder, onToggleFolderFold]);

    useEffect(
        () => () => {
            cancelDragHoverExpand();
            // 낙관 반영으로 행이 사라지면 소유권만 남아 다음 드래그의 첫 claim이 죽은 setter를 호출한다
            if (activeDropRowRef?.current?.folderId === folder?.id) activeDropRowRef.current = null;
        },
        [cancelDragHoverExpand, activeDropRowRef, folder?.id],
    );

    const handleDragOver = useCallback(
        (e) => {
            const dragKind = getDroppableDragKind(e);
            if (!dragKind) {
                // 드래그 도중 이 행이 드롭 불가로 전환되면(이동 요청 등록 등) onDragLeave·onDrop도 함께 떨어져 나가
                // 정리 기회가 사라지므로, 켜져 있던 피드백은 여기서 거둔다
                if (dropPosition) resetDropFeedback();
                return;
            }
            e.preventDefault();
            e.stopPropagation();

            if (dragKind === "item") {
                e.dataTransfer.dropEffect = "move";
                claimDropFeedback("into");
                scheduleDragHoverExpand();
                return;
            }

            if (isInvalidFolderDropTarget()) {
                e.dataTransfer.dropEffect = "none";
                resetDropFeedback();
                return;
            }

            e.dataTransfer.dropEffect = "move";
            // 드롭존 3분할: 상단 25% 앞 삽입 / 중앙 65% 하위 이동 / 하단 10% 뒤 삽입.
            // 드라이브(루트)는 재정렬 대상이 아니므로 앞/뒤 삽입 없음.
            const rect = e.currentTarget.getBoundingClientRect();
            const ratio = (e.clientY - rect.top) / rect.height;
            let position = "into";
            if (folder.depth > 0 && ratio < 0.25) {
                position = "before";
                // 하위 로딩 중(pending)엔 after 인디케이터가 행과 스켈레톤 경계에 그려져 스켈레톤에 드롭되는 것처럼 보이므로 하단 존은 into 유지
            } else if (ratio > 0.9 && !folder.pending) {
                // 펼쳐져 자식이 보이면 첫 자식 앞(intoFirst), 아니면 다음 형제 — 루트(depth 0)는 형제가 없어 하위 이동 유지
                if (!folder.isFold && folder.children?.length > 0) {
                    position = "intoFirst";
                } else if (folder.depth > 0) {
                    position = "after";
                }
            }
            claimDropFeedback(position);
            if (position === "into") {
                scheduleDragHoverExpand();
            } else {
                cancelDragHoverExpand();
            }
        },
        [
            getDroppableDragKind,
            folder,
            dropPosition,
            isInvalidFolderDropTarget,
            scheduleDragHoverExpand,
            cancelDragHoverExpand,
            claimDropFeedback,
            resetDropFeedback,
        ],
    );

    const handleDragLeave = useCallback(
        (e) => {
            if (!isItemDroppable && !isFolderDroppable) return;
            e.preventDefault();
            e.stopPropagation();
            // 행 내부 자식 요소로의 이동은 이탈이 아님 — 인디케이터 깜빡임 방지
            if (e.currentTarget.contains(e.relatedTarget)) return;
            resetDropFeedback();
        },
        [isItemDroppable, isFolderDroppable, resetDropFeedback],
    );

    const handleDrop = useCallback(
        (e) => {
            const dragKind = getDroppableDragKind(e);
            if (!dragKind) return;
            e.preventDefault();
            e.stopPropagation();
            const position = dropPosition;
            resetDropFeedback();

            if (dragKind === "folder") {
                const draggedId = draggingFolderIdRef?.current;
                if (!position || isInvalidFolderDropTarget()) return;
                // 존 판정에 이어 드롭의 데이터적 의미(부모·순서·원위치·move API 본문)까지 해석해 완성된 plan만 상위로 전달
                const plan = resolveFolderDropPlan(folderMap, draggedId, folder, position);
                if (!plan) return;
                // 낙관 반영·detach로 소스 행이 즉시 언마운트되면 dragend가 오지 않으므로 넘기기 전에 정리
                if (draggingFolderIdRef) draggingFolderIdRef.current = null;
                onDropFolderItem(plan);
                return;
            }

            if (folder.id === selectedFolderId) return;
            onDropTreeItem(folder);
        },
        [
            getDroppableDragKind,
            folder,
            folderMap,
            dropPosition,
            selectedFolderId,
            onDropTreeItem,
            onDropFolderItem,
            draggingFolderIdRef,
            isInvalidFolderDropTarget,
            resetDropFeedback,
        ],
    );

    // 드래그 소스(폴더 이동 시작)와 드롭 타깃(폴더·목록 아이템 수용)은 활성 조건이 달라 묶음 단위로 부착
    const dragProps = {
        draggable: isFolderDraggable,
        ...(isFolderDraggable && { onDragStart: handleDragStart, onDragEnd: handleDragEnd }),
        ...((isItemDroppable || isFolderDroppable) && {
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            onDrop: handleDrop,
        }),
    };

    return { dragProps, isIntoDropTarget, dropPosition, isDragSource };
};

export default useTreeItemDnd;
